import { z } from 'zod';
/**
 * Append-only audit log. Spec §4.1, Appendix C. The `marketplace_app` DB role
 * has INSERT-only on this table; UPDATE and DELETE are revoked.
 */
export const auditActionSchema = z.enum([
    'submission.created',
    'submission.upload_failed',
    'submission.requeued',
    'submission.approved',
    'submission.approve_failed',
    'submission.rejected',
    'submission.auto_approved',
    'submission.forced_scan_failed',
    'plugin.updated',
    'plugin.deprecated',
    'plugin.yanked',
    'plugin.unyanked',
    'plugin.unmark_deprecated',
    'plugin.synced',
    'plugin.hub_listed',
    'plugin.hub_unlisted',
    // sync-repo skipped a plugin whose name is owned by another source (or a
    // soft-deleted row — never resurrected). Audit-without-mutation: records
    // the rejection; the existing row is untouched. Resource id = the
    // name-holding row's plugin id.
    'plugin.sync_collision_skipped',
    // reconcile-sync detected the row's open Hub PR was abandoned in ADO.
    // Row stays hub-pending (hidden); operators see it via the admin hub queue.
    'plugin.hub_pr_abandoned',
    'team.created',
    'team.updated',
    'team.deleted',
    'team.admin_granted',
    'team.admin_revoked',
    'team.reprovision_run',
    'team.reprovisioned',
    'team_reviewer.granted',
    'team_reviewer.revoked',
    // Plain team-member tier (non-RBAC, non-ADO) — POST/DELETE
    // /api/teams/:id/members. Shares `team_associations` with reviewers.
    'team_member.granted',
    'team_member.revoked',
    'user.created',
    'user.role_added',
    'user.role_removed',
    'user.trusted_toggled',
    'user.ado_identity_resolved',
    'user.ado_identity_overridden',
    // `flag.created` is intentionally absent — flags are seeded via
    // `db/seeds/feature-flags.ts`, never runtime-created (spec lines 927-930).
    'flag.updated',
    'flag.deleted',
    'role.updated',
    'source.provisioned',
    'source.provision_failed',
    'source.provision_retried',
    'source.webhook_rotated',
    'source.webhook_rotation_failed',
    'source.webhook_registered',
    'source.renamed_absorbed',
    'source.template_refreshed',
    // reconcile-sync retires a source whose ADO repo was deleted (404/TF401019):
    // soft-delete + status='failed' + auto-yank plugins. See the reconcile-sync worker.
    'source.archived_repo_gone',
    // POST /api/sources/:id/reactivate restores a repo-gone-retired source once
    // its ADO repo reappears: clears deleted_at, status='active', un-yanks plugins.
    'source.reactivated',
    // POST /api/admin/sync/sweep opened a manual reconcile sweep (resource id =
    // sweep id). Spec 2026-06-15 §3.
    'admin.sync_sweep',
    // POST /api/admin/sync/sources/:id force-resynced one source (resource id =
    // source id). Spec 2026-06-15 §3.
    'admin.sync_source',
    // Emitted by `/webhooks/ado-hub` when the mirror declines to reconcile
    // (manifest parse/schema failure or unexpected error). Records the
    // skip decision without a paired state mutation — see ADR-0003.
    'hub.mirror_skipped',
    // Emitted by PUT /api/banner when an admin updates the sidebar banner.
    // The audited row IS the `sidebar-banner` feature_flags row, so it reuses
    // AuditResourceType.flag with resourceId 'sidebar-banner' (no new resource type).
    'banner.updated',
    // Emitted by `openMembershipSyncRun` (Task 5) when a cron/manual/chained
    // ADO-authoritative membership-sync run is opened. Resource id =
    // `team_membership_sync_runs` row. Distinct from `team.reprovision_run`,
    // which pushes marketplace state OUT to ADO; this run adopts ADO-side
    // membership changes INTO the marketplace DB.
    'team.membership_sync_run',
    // Emitted by POST /api/admin/hub-queue/:pluginId/force-auto-complete (Task
    // 10) — the operator escape hatch that turns the manual PATCH used to
    // unblock four stalled Hub PRs on 2026-07-29 into a supported action.
    // Written AFTER the ADO PATCH succeeds so the row never asserts a change
    // that didn't happen. Resource id = the plugin id (resolved via
    // `plugins.hub_pr_url`, never the raw Hub PR id).
    'plugin.hub_pr_force_auto_complete',
    // Emitted by POST /api/admin/sync/contributions — the operator trigger for
    // an immediate `collect-contributions` run (rollout step 2's first backfill,
    // and re-collection after an ADO outage). Written in the same transaction
    // as nothing else: the mutation it records is ADO-read + fact-table write
    // performed asynchronously by the worker, so the row asserts the REQUEST,
    // and its resource id is the enqueued job id. Appended at the END of the
    // enum — the openapi.json enum array is order-sensitive.
    'admin.collect_contributions',
]);
/**
 * Importable const enum. Mirrors `marketplace-api/src/lib/audit-actions.ts`
 * so audit producers can reference `AuditResourceType.flag` directly
 * instead of retyping the literal — the bug class that bred the original
 * `'feature_flag'` / `'plugin_source'` drift.
 *
 * Key declaration order is load-bearing: `Object.values()` preserves it,
 * and the openapi.json enum array is order-sensitive. `pnpm contracts:check`
 * fails on any reordering. Match the existing wire order exactly.
 */
export const AuditResourceType = {
    submission: 'submission',
    plugin: 'plugin',
    team: 'team',
    user: 'user',
    flag: 'flag',
    source: 'source',
    role: 'role',
    /**
     * Synthetic resource type for `hub.mirror_skipped` audit rows — the
     * Hub manifest is not a row in `plugins` or `plugin_sources`, so we key
     * the audit on the Hub repo id directly. ADR-0003 / T6.
     */
    hub_manifest: 'hub_manifest',
    /**
     * Synthetic resource type for `admin.sync_sweep` audit rows — a reconcile
     * sweep is a `sync_sweeps` row, not a `plugin_sources` row, so the audit is
     * keyed on the sweep id directly (a sweep id is NOT a source id). Spec
     * 2026-06-15 §3. The per-source force re-sync (`admin.sync_source`) keeps
     * `source` — its resource id IS a `plugin_sources` row.
     */
    sweep: 'sweep',
    /**
     * Synthetic resource type for `team.reprovision_run` audit rows — a run is a
     * `team_reprovision_runs` row, not a `teams` row. Per-team `team.reprovisioned`
     * rows key on the `teams` row and use `team`.
     */
    team_reprovision_run: 'team_reprovision_run',
    /**
     * Synthetic resource type for `team.membership_sync_run` audit rows — a run
     * is a `team_membership_sync_runs` row, not a `teams` row. Mirrors
     * `team_reprovision_run` exactly, on the inverted (ADO→PG) sync.
     */
    team_membership_sync_run: 'team_membership_sync_run',
    /**
     * Synthetic resource type for `admin.collect_contributions` audit rows — a
     * collection run spans every active source at once, so there is no single
     * `plugin_sources` row to key on. Resource id = the enqueued BullMQ job id,
     * which is what an operator correlates against the worker's logs.
     */
    contributions_run: 'contributions_run',
};
export const ALL_AUDIT_RESOURCE_TYPES = Object.values(AuditResourceType);
// Cast to the typed-tuple form so `z.enum(...)` infers the narrow union
// rather than widening back to `string`. Without this the schema's
// `.options` types as `string[]` and `.parse()` returns `string` —
// re-erasing the type discipline the const enum was added to enforce.
export const auditResourceTypeSchema = z.enum(ALL_AUDIT_RESOURCE_TYPES);
export const auditLogSchema = z.object({
    id: z.string().uuid(),
    actorId: z.string().uuid(),
    action: auditActionSchema,
    resourceType: auditResourceTypeSchema,
    resourceId: z.string().min(1),
    before: z.unknown().nullable(),
    after: z.unknown().nullable(),
    correlationId: z.string().nullable(),
    createdAt: z.string().datetime(),
});
//# sourceMappingURL=audit.js.map